import React, { useEffect, useState } from 'react';
import { watchAccessible } from '../../services/recordAccess';
import { useAuth } from '../../context/AuthContext';
import { CONTACT_TYPES } from '../../services/pipelineContact';

export { CONTACT_TYPES };

// Multi-select chips for contact type (Buyer, Seller, Tenant, Landlord, Agent, Vendor, Other).
export function ContactTypeChips({ value, onChange }) {
  const list = Array.isArray(value) ? value : [];
  const toggle = (t) => onChange(list.includes(t) ? list.filter((x) => x !== t) : [...list, t]);
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }} role="group" aria-label="Contact type">
      {CONTACT_TYPES.map((t) => {
        const on = list.includes(t);
        return (
          <button key={t} type="button" aria-pressed={on} onClick={() => toggle(t)}
            style={{ padding: '6px 12px', borderRadius: 16, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: '1px solid #B8962E', background: on ? '#B8962E' : '#FFFFFF', color: on ? '#FFFFFF' : '#1A1A1A' }}>
            {on ? '\u2713 ' : ''}{t}
          </button>
        );
      })}
    </div>
  );
}

const digits = (v) => String(v || '').replace(/\D/g, '').slice(-10);

// Pipeline records for contacts: linked contactId (either direction) first, then email or phone.
export const usePipelineRows = () => {
  const [sellers, setSellers] = useState([]);
  const [buyers, setBuyers] = useState([]);
  const { user, userRole } = useAuth();
  const isAdmin = userRole === 'Admin';
  const viewAll = isAdmin || userRole === 'Marketing Manager';
  useEffect(() => watchAccessible('sellerInterests', user?.uid, viewAll, setSellers), [user?.uid, viewAll]);
  useEffect(() => watchAccessible('buyerInterests', user?.uid, viewAll, setBuyers), [user?.uid, viewAll]);
  return { sellers, buyers };
};
export const matchPipeline = (contact, { sellers, buyers }) => {
  if (!contact) return [];
  const email = String(contact.email || '').trim().toLowerCase(); const phone = digits(contact.phone);
  const find = (rows, linkKey) => {
    const linked = rows.filter((r) => r.contactId === contact.id || (contact[linkKey] || {})[r.id]);
    if (linked.length) return linked;
    return rows.filter((r) => (email && String(r.email || '').trim().toLowerCase() === email) || (phone.length === 10 && digits(r.phone) === phone));
  };
  return [...find(sellers, 'sellerInterestIds').map((r) => ({ ...r, kindLabel: 'Seller' })), ...find(buyers, 'buyerInterestIds').map((r) => ({ ...r, kindLabel: 'Buyer' }))];
};

// Prominent, clickable "In pipeline" banner (opens the record on the Pipeline page).
// Client roles on the contact: one badge per role (Buyer / Seller / Tenant / Landlord), each linking
// to the pipeline record that holds it. One contact, several roles; never duplicate contacts.
const rolesOfHit = (r) => { const t = Array.isArray(r?.pipelineTypes) ? r.pipelineTypes : Object.keys(r?.pipelineTypes || {}); return t.length ? t : [r.kindLabel]; };
export function PipelineBanner({ hits }) {
  if (!hits || !hits.length) return null;
  const roles = [];
  hits.forEach((r) => rolesOfHit(r).forEach((t) => { if (!roles.some((x) => x.t === t)) roles.push({ t, r }); }));
  const order = ['Buyer', 'Seller', 'Tenant', 'Landlord'];
  roles.sort((a, b) => order.indexOf(a.t) - order.indexOf(b.t));
  return (
    <div style={{ margin: '0 0 12px', padding: '10px 12px', borderRadius: 10, background: '#1A1A1A', border: '2px solid #D4AF37' }}>
      <div style={{ color: '#D4AF37', fontWeight: 800, fontSize: 14, marginBottom: 8 }}>IN PIPELINE: CLIENT ROLES</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {roles.map(({ t, r }) => (
          <a key={t} href={`/pipeline?open=${r.id}`} title={r.propertyAddress || ''}
            style={{ display: 'inline-flex', alignItems: 'center', minHeight: 40, padding: '6px 14px', borderRadius: 20, background: '#D4AF37', color: '#1A1A1A', fontWeight: 800, fontSize: 14, textDecoration: 'none' }}>
            {t.toUpperCase()} {'\u2192'}
          </a>
        ))}
      </div>
    </div>
  );
}
